import React, {useState} from 'react';
import {Container, Row, Col, Input, Label} from 'reactstrap';
import {CssMasonry} from '../../../components/Masonry/CssMasonry';
import JuryProjectCard from './JuryProjectCard/JuryProjectCard';
import styles from './JuryProjectsList.module.scss';
import useJuryStore from '../../../store/juryStore';

const PROJECTS_PER_PAGE = 50;

/**
 * Shared grid UI for jury project views (Teilnehmer + Merkliste).
 * Reads votes, bookmarks and actions directly from the Zustand store —
 * only `projects` is passed as a prop (to allow pre-filtering per view).
 */
function JuryProjectsGrid({
                              projects,
                              showCount = true,
                              showDeleteButton = false,
                              emptyMessage = 'Keine Projekte gefunden.',
                          }) {
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [sortBy, setSortBy] = useState('name');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // Store
    const loading = useJuryStore(s => s.loading);
    const categoryOptions = useJuryStore(s => s.categoryOptions);

    React.useEffect(() => {
        setCurrentPage(1);
    }, [categoryFilter, sortBy, searchTerm]);

    const categories = categoryOptions.length > 0
        ? categoryOptions
        : [...new Set(projects.map(p => p.projectCategory).filter(Boolean))].sort()
            .map(value => ({value, displayValue: value}));

    const filteredProjects = projects
        .filter(project => {
            if (categoryFilter !== 'all' && project.projectCategory !== categoryFilter) return false;
            if (searchTerm) {
                const lower = searchTerm.toLowerCase();
                return project.projectTitle?.toLowerCase().includes(lower)
                    || project.contestantName?.toLowerCase().includes(lower);
            }
            return true;
        })
        .sort((a, b) => {
            if (sortBy === 'name') return (a.contestantName || '').localeCompare(b.contestantName || '');
            if (sortBy === 'title') return (a.projectTitle || '').localeCompare(b.projectTitle || '');
            if (sortBy === 'votes') return (b.voteCount || 0) - (a.voteCount || 0);
            return 0;
        });

    const totalPages = Math.ceil(filteredProjects.length / PROJECTS_PER_PAGE);
    const currentProjects = filteredProjects.slice(
        (currentPage - 1) * PROJECTS_PER_PAGE,
        currentPage * PROJECTS_PER_PAGE
    );

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({top: 0, behavior: 'smooth'});
    };

    return (
        <Container className="my-4">
            <Row className="mb-4">
                <Col>
                    <h2 className={styles.PageTitle}>Teilnehmer suchen</h2>
                    <div className={styles.SearchBar}>
                        <Input
                            type="text"
                            placeholder="Suche nach Projekten oder Institutionen..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles.SearchInput}
                        />
                    </div>
                </Col>
            </Row>

            {showCount && (
                <Row className="mb-3">
                    <Col>
                        <p className={styles.ProjectCount}>
                            Wir freuen uns über {filteredProjects.length} Teilnehmer!
                            {filteredProjects.length > PROJECTS_PER_PAGE && (
                                <span className={styles.PageInfo}>
                                    {' '}(Seite {currentPage} von {totalPages})
                                </span>
                            )}
                        </p>
                    </Col>
                </Row>
            )}

            <Row className="mb-4">
                <Col md={4} className="mb-2 mb-md-0">
                    <Label className={styles.FilterLabel}>Kategorie</Label>
                    <Input
                        type="select"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className={styles.FilterSelect}
                    >
                        <option value="all">Alle</option>
                        {categories.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.displayValue}</option>
                        ))}
                    </Input>
                </Col>
                <Col md={4}>
                    <Label className={styles.FilterLabel}>Sortieren</Label>
                    <Input
                        type="select"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className={styles.FilterSelect}
                    >
                        <option value="name">Teilnehmername</option>
                        <option value="title">Projekttitel</option>
                        <option value="votes">Stimmen</option>
                    </Input>
                </Col>
            </Row>

            {loading ? (
                <Row>
                    <Col className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Lädt...</span>
                        </div>
                    </Col>
                </Row>
            ) : (
                <>
                    <CssMasonry
                        columnGap="1.5rem"
                        rowGap="1.5rem"
                        breakpoints={{0: 1, 768: 2, 1200: 3}}
                    >
                        {currentProjects.map(project => (
                            <JuryProjectCard
                                key={project._id}
                                project={project}
                                showDeleteButton={showDeleteButton}
                            />
                        ))}
                    </CssMasonry>

                    {totalPages > 1 && (
                        <Row className="mt-5">
                            <Col>
                                <nav className={styles.Pagination}>
                                    <button
                                        className={styles.PageButton}
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        ← Zurück
                                    </button>
                                    <div className={styles.PageNumbers}>
                                        {[...Array(totalPages)].map((_, index) => {
                                            const pageNumber = index + 1;
                                            if (
                                                pageNumber === 1 ||
                                                pageNumber === totalPages ||
                                                (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
                                            ) {
                                                return (
                                                    <button
                                                        key={pageNumber}
                                                        className={`${styles.PageNumber} ${currentPage === pageNumber ? styles.PageNumberActive : ''}`}
                                                        onClick={() => handlePageChange(pageNumber)}
                                                    >
                                                        {pageNumber}
                                                    </button>
                                                );
                                            } else if (pageNumber === currentPage - 3 || pageNumber === currentPage + 3) {
                                                return <span key={pageNumber}
                                                             className={styles.PageEllipsis}>...</span>;
                                            }
                                            return null;
                                        })}
                                    </div>
                                    <button
                                        className={styles.PageButton}
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        Weiter →
                                    </button>
                                </nav>
                            </Col>
                        </Row>
                    )}

                    {filteredProjects.length === 0 && (
                        <p className="mt-3">{emptyMessage}</p>
                    )}
                </>
            )}
        </Container>
    );
}

export default JuryProjectsGrid;
